/**
 * ChatDragController — Chat "drawer" resize system
 *
 * The visible chat (.chat-zone) is pinned to the bottom-center of the screen.
 * Its text input + suggestion chips stay anchored at the bottom AT ALL TIMES —
 * they never slide off-screen. Only the MESSAGE THREAD (.chat-thread) is
 * resizable: a grab handle that rides the TOP of the messages lets the user
 * drag the message window taller (see more history) or shorter (reveal the
 * 3D character's face behind the chat). Dragging all the way down collapses
 * the thread to nothing, leaving a clean view of the character with just the
 * input bar.
 *
 * Why a drawer and not "move the whole panel": the old design changed the
 * panel's `bottom`, which dragged the input (the bottom-most element) off the
 * screen — the user lost the very control they needed. Resizing the thread
 * keeps the input reachable while still uncovering the character.
 *
 * Public API (window.chatDragController):
 *   - getHeight() / setHeight(px, animate)
 *   - expand() / collapse() / resetHeight()
 *   - enable() / disable()
 */

export class ChatDragController {
    constructor(options = {}) {
        this.config = {
            chatSelector: options.chatSelector || ".chat-zone",
            threadSelector:
                options.threadSelector || ".chat-thread, .chat-history",
            dragHandleSelector:
                options.dragHandleSelector || ".chat-drag-handle",
            storageKey: options.storageKey || "chatThreadHeight",
            // Minimum thread cap (px). 0 = fully collapsed (face fully revealed).
            minHeight: options.minHeight ?? 0,
            enablePersistence: options.enablePersistence !== false,
            onResize: options.onResize || null,
        };

        // Elements
        this.chatZone = null;
        this.thread = null;
        this.dragHandle = null;
        this.classObserver = null;

        // State
        this.isDragging = false;
        this.isEnabled = true;
        this.startY = 0;
        this.startHeight = 0;
        this.currentHeight = null; // current applied cap in px

        // Bind handlers
        this.handleResize = this.handleResize.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleOutsideTap = this.handleOutsideTap.bind(this);

        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () =>
                this.initialize(),
            );
        } else {
            this.initialize();
        }
    }

    /* ── Sizing helpers ─────────────────────────────────────── */

    /** Largest the message window may grow — capped so it never swallows the
     *  upper screen (the character's face). vh-based so it adapts to phones. */
    maxHeight() {
        return Math.min(Math.round(window.innerHeight * 0.45), 360);
    }

    /** Default open height when there is no saved preference. */
    defaultHeight() {
        return Math.min(Math.round(window.innerHeight * 0.4), 320);
    }

    /* ── Init ───────────────────────────────────────────────── */

    initialize() {
        this.chatZone = document.querySelector(this.config.chatSelector);
        if (!this.chatZone) {
            console.warn("ChatDragController: chat zone not found");
            return;
        }

        this.thread = this.chatZone.querySelector(this.config.threadSelector);
        if (!this.thread) {
            console.warn("ChatDragController: message thread not found");
            return;
        }

        this.createDragHandle();
        this.injectStyles();

        // Apply saved or default height
        if (this.config.enablePersistence) {
            this.loadHeight();
        } else {
            this.setHeight(this.defaultHeight(), false);
        }

        this.attachEventListeners();
        this.observeThreadMessages();
        window.addEventListener("resize", this.handleResize);
        this.initKeyboardHandling();
        this.initTapOutsideBlur();

        window.chatDragController = this;
        console.log("ChatDragController: initialized (drawer mode)");
    }

    /* ── Handle element ─────────────────────────────────────── */

    createDragHandle() {
        let existing = this.chatZone.querySelector(
            this.config.dragHandleSelector,
        );
        if (existing) {
            this.dragHandle = existing;
            return;
        }

        this.dragHandle = document.createElement("div");
        this.dragHandle.className = "chat-drag-handle";
        this.dragHandle.setAttribute("role", "separator");
        this.dragHandle.setAttribute("aria-orientation", "horizontal");
        this.dragHandle.setAttribute(
            "aria-label",
            "Drag to resize the chat, or reveal the character",
        );
        this.dragHandle.setAttribute("title", "Drag to resize the chat");
        this.dragHandle.innerHTML = `
            <div class="drag-indicator"><span class="drag-bar"></span></div>
        `;

        // Sits as the FIRST child so it rides the top edge of the message
        // thread (which is the next child). As the thread shrinks, the handle
        // rides down with it, staying glued just above the messages.
        this.chatZone.insertBefore(this.dragHandle, this.chatZone.firstChild);
    }

    injectStyles() {
        if (document.getElementById("chat-drag-styles")) return;

        const styles = document.createElement("style");
        styles.id = "chat-drag-styles";
        styles.textContent = `
            /* The chat zone itself is click-through; only its real controls
               capture touches. This keeps the empty space around the chat from
               blocking finger-rotation of the 3D character behind it. */
            .chat-zone { pointer-events: none; }
            .chat-zone > * { pointer-events: auto; }
            /* The suggestion-chip row passes touches through its gaps so the
               character is rotatable between chips; the chips stay tappable. */
            .chat-zone > .prompt-row { pointer-events: none; }
            .chat-zone > .prompt-row > * { pointer-events: auto; }
            /* An empty thread (decorative placeholder only) must not block
               rotation either. */
            .chat-thread:not(.has-messages) { pointer-events: none; }

            /* Drag handle — ALWAYS-visible grab pill glued to the top of the
               messages. No hover gate (touch has no hover). Big touch target
               (~44px) per Apple HIG, slim visible grip. */
            .chat-drag-handle {
                flex: 0 0 auto;
                align-self: center;
                padding: 13px 30px;
                margin: 0 auto -8px;
                cursor: ns-resize;
                z-index: 1001;
                display: flex;
                align-items: center;
                justify-content: center;
                background: transparent;
                touch-action: none;
                -webkit-tap-highlight-color: transparent;
            }
            .chat-drag-handle .drag-indicator {
                display: flex; align-items: center; justify-content: center;
                background: transparent;
            }
            .chat-drag-handle .drag-bar {
                width: 44px; height: 5px;
                background: var(--text-dim, rgba(255, 255, 255, 0.4));
                border-radius: 3px;
                transition: background-color 0.2s ease, width 0.2s ease;
            }
            .chat-drag-handle:hover .drag-bar {
                background: var(--warm-glow, rgba(255, 255, 255, 0.6));
                width: 52px;
            }
            .chat-zone.dragging .drag-bar {
                background: var(--warm-glow, rgba(255, 255, 255, 0.78));
                width: 52px;
            }
            .chat-zone.dragging { cursor: ns-resize; }
            .chat-zone.dragging * { cursor: ns-resize !important; }

            /* Collapsed thread: drop its padding so it truly disappears. */
            .chat-thread.is-collapsed { padding-top: 0; padding-bottom: 0; }

            @media (max-width: 768px) {
                /* Extra-generous grab area on touch: a missed re-grab lands
                   on chat-zone padding/canvas, which consumes no vertical
                   movement — Safari then rubber-bands the document into
                   pull-to-refresh. Bigger target = fewer missed grabs. */
                .chat-drag-handle { padding: 20px 56px; margin-bottom: -14px; }
                .chat-drag-handle .drag-bar { width: 48px; height: 6px; }
                .chat-drag-handle:hover .drag-bar,
                .chat-zone.dragging .drag-bar { width: 56px; }
            }
        `;
        document.head.appendChild(styles);
    }

    /* ── Height application ─────────────────────────────────── */

    /** Read the cap currently applied to the thread (px). */
    getCurrentCap() {
        if (this.currentHeight != null) return this.currentHeight;
        const inline = parseFloat(this.thread.style.maxHeight);
        if (!Number.isNaN(inline)) return inline;
        return this.defaultHeight();
    }

    setHeight(px, animate = true) {
        if (!this.thread) return;
        const clamped = Math.max(
            this.config.minHeight,
            Math.min(this.maxHeight(), Math.round(px)),
        );
        this.currentHeight = clamped;

        // Animate only outside of an active drag (smooth expand/collapse);
        // instant while a finger/mouse is moving so it tracks 1:1.
        this.thread.style.transition = animate
            ? "max-height 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
            : "none";
        this.thread.style.maxHeight = `${clamped}px`;
        this.thread.classList.toggle("is-collapsed", clamped <= 4);

        // Keep the newest message in view as the window resizes.
        this.thread.scrollTop = this.thread.scrollHeight;

        if (this.config.onResize) {
            this.config.onResize({ height: clamped, animated: animate });
        }
        window.dispatchEvent(
            new CustomEvent("chatThreadResized", {
                detail: { height: clamped },
            }),
        );
    }

    getHeight() {
        return this.getCurrentCap();
    }

    expand(animate = true) {
        this.setHeight(this.maxHeight(), animate);
        this.saveHeight();
    }

    collapse(animate = true) {
        this.setHeight(this.config.minHeight, animate);
        this.saveHeight();
    }

    resetHeight() {
        this.setHeight(this.defaultHeight(), true);
        if (this.config.enablePersistence) {
            localStorage.removeItem(this.config.storageKey);
        }
    }

    /* ── Drag lifecycle ─────────────────────────────────────── */

    startDrag(clientY) {
        this.isDragging = true;
        this.startY = clientY;
        this.startHeight = this.getCurrentCap();
        this.chatZone.classList.add("dragging");
    }

    updateDrag(clientY) {
        // Dragging the handle DOWN (clientY increases) shrinks the message
        // window (reveals the face); dragging UP grows it.
        const delta = clientY - this.startY;
        this.setHeight(this.startHeight - delta, false);
    }

    endDrag() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.chatZone.classList.remove("dragging");
        this.saveHeight();
    }

    /* ── Event wiring ───────────────────────────────────────── */

    attachEventListeners() {
        if (!this.dragHandle) return;
        this.dragHandle.addEventListener("mousedown", this.handleMouseDown);
        this.dragHandle.addEventListener("touchstart", this.handleTouchStart, {
            passive: false,
        });
        // Double-click / double-tap the handle = quick reset to default.
        this.dragHandle.addEventListener("dblclick", () => this.resetHeight());
    }

    handleMouseDown(e) {
        if (!this.isEnabled) return;
        e.preventDefault();
        this.startDrag(e.clientY);
        document.addEventListener("mousemove", this.handleMouseMove);
        document.addEventListener("mouseup", this.handleMouseUp);
    }

    handleMouseMove(e) {
        if (!this.isDragging) return;
        e.preventDefault();
        this.updateDrag(e.clientY);
    }

    handleMouseUp() {
        this.endDrag();
        document.removeEventListener("mousemove", this.handleMouseMove);
        document.removeEventListener("mouseup", this.handleMouseUp);
    }

    handleTouchStart(e) {
        if (!this.isEnabled) return;
        if (e.touches.length !== 1) return;
        e.preventDefault();
        this.startDrag(e.touches[0].clientY);
        document.addEventListener("touchmove", this.handleTouchMove, {
            passive: false,
        });
        document.addEventListener("touchend", this.handleTouchEnd);
        document.addEventListener("touchcancel", this.handleTouchEnd);
    }

    handleTouchMove(e) {
        if (!this.isDragging) return;
        // ALWAYS consume the gesture while a drag is active — even if a 2nd
        // finger lands. Returning without preventDefault here hands the
        // gesture to Safari, whose rubber-band becomes pull-to-refresh and
        // reloads the whole Lab mid-drag (iPhone force-reload trigger).
        e.preventDefault();
        if (e.touches.length !== 1) return;
        this.updateDrag(e.touches[0].clientY);
    }

    handleTouchEnd() {
        this.endDrag();
        document.removeEventListener("touchmove", this.handleTouchMove);
        document.removeEventListener("touchend", this.handleTouchEnd);
        document.removeEventListener("touchcancel", this.handleTouchEnd);
    }

    /* ── Viewport / keyboard ────────────────────────────────── */

    handleResize() {
        clearTimeout(this._resizeTimeout);
        this._resizeTimeout = setTimeout(() => {
            // Re-clamp to the new viewport so a tall saved value can't push
            // the chat past the new screen height.
            this.setHeight(this.getCurrentCap(), false);
        }, 100);
    }

    /**
     * Lift the chat above the iOS on-screen keyboard. A fixed, bottom-anchored
     * element on iOS Safari stays pinned behind the keyboard; the visual
     * viewport tells us how much of the screen the keyboard is covering, and we
     * offset the chat by that much via a CSS var (so we never fight the panel's
     * own transform-based slide animations).
     */
    initKeyboardHandling() {
        const vv = window.visualViewport;
        if (!vv) return;
        vv.addEventListener("resize", this.handleViewportChange);
        vv.addEventListener("scroll", this.handleViewportChange);
    }

    handleViewportChange() {
        const vv = window.visualViewport;
        if (!vv || !this.chatZone) return;
        const overlap = Math.max(
            0,
            window.innerHeight - vv.height - vv.offsetTop,
        );
        // Treat only a sizable overlap as a keyboard (ignore browser-chrome
        // jitter). Offset just shy of the keyboard so the input clears it.
        const kb = overlap > 120 ? overlap : 0;
        this.chatZone.style.setProperty("--kb-offset", `${kb}px`);
    }

    /**
     * Universal "close the keyboard" escape: tapping anywhere OUTSIDE the chat
     * area (the 3D character / empty scene) blurs the chat input, which on iOS
     * dismisses the on-screen keyboard. Without this, tapping the WebGL canvas
     * (a non-focusable element) leaves the input focused and the keyboard stuck.
     * No-op when the input isn't focused, and never steals focus from other
     * text fields (e.g. the sign-in modal's email/password).
     */
    initTapOutsideBlur() {
        document.addEventListener("pointerdown", this.handleOutsideTap, {
            passive: true,
        });
    }

    handleOutsideTap(e) {
        const input = document.getElementById("chat-input");
        if (!input || document.activeElement !== input) return;
        const t = e.target;
        if (!(t instanceof Element)) {
            input.blur();
            return;
        }
        // Leave taps inside the chat zone (input, send, chips, thread) alone,
        // and never interfere with other editable fields.
        if (t.closest(".chat-zone")) return;
        if (t.closest("input, textarea, [contenteditable]")) return;
        input.blur();
    }

    /* ── Thread message observer ────────────────────────────── */

    /**
     * Toggle `has-messages` on the thread based on whether REAL messages exist
     * (any child that isn't the decorative `.chat-empty` placeholder). This is
     * owned HERE — not by ChatController — so the empty-state pass-through
     * (`.chat-thread:not(.has-messages){pointer-events:none}`) and the empty
     * placeholder visibility stay correct no matter which path renders a message
     * (the real ChatController stream, the greeting, or the mock bridge). The
     * live ChatController appends straight to #chat-history without setting the
     * class, so relying on it would leave the thread permanently click-through
     * (unscrollable). Note: NO scroll-to-bottom here — Option 1 keeps a new
     * reply anchored at its top.
     */
    syncHasMessages() {
        if (!this.thread) return;
        const hasMsgs = Array.from(this.thread.children).some(
            (el) => !el.classList.contains("chat-empty"),
        );
        this.thread.classList.toggle("has-messages", hasMsgs);
    }

    observeThreadMessages() {
        this.syncHasMessages();
        this.classObserver = new MutationObserver(() => this.syncHasMessages());
        this.classObserver.observe(this.thread, { childList: true });
    }

    /* ── Persistence ────────────────────────────────────────── */

    saveHeight() {
        if (!this.config.enablePersistence) return;
        try {
            localStorage.setItem(
                this.config.storageKey,
                JSON.stringify({
                    height: this.currentHeight,
                    timestamp: Date.now(),
                }),
            );
        } catch (e) {
            console.warn("ChatDragController: failed to save height", e);
        }
    }

    loadHeight() {
        try {
            const saved = localStorage.getItem(this.config.storageKey);
            if (saved) {
                const data = JSON.parse(saved);
                if (typeof data.height === "number") {
                    this.setHeight(data.height, false);
                    return;
                }
            }
        } catch (e) {
            console.warn("ChatDragController: failed to load height", e);
        }
        this.setHeight(this.defaultHeight(), false);
    }

    /* ── Enable / disable / teardown ────────────────────────── */

    enable() {
        this.isEnabled = true;
        if (this.dragHandle) this.dragHandle.style.display = "flex";
    }

    disable() {
        this.isEnabled = false;
        if (this.dragHandle) this.dragHandle.style.display = "none";
    }

    isDraggingActive() {
        return this.isDragging;
    }

    destroy() {
        if (this.dragHandle) {
            this.dragHandle.removeEventListener(
                "mousedown",
                this.handleMouseDown,
            );
            this.dragHandle.removeEventListener(
                "touchstart",
                this.handleTouchStart,
            );
            this.dragHandle.remove();
        }
        if (this.classObserver) this.classObserver.disconnect();
        window.removeEventListener("resize", this.handleResize);
        const vv = window.visualViewport;
        if (vv) {
            vv.removeEventListener("resize", this.handleViewportChange);
            vv.removeEventListener("scroll", this.handleViewportChange);
        }
        document.removeEventListener("mousemove", this.handleMouseMove);
        document.removeEventListener("mouseup", this.handleMouseUp);
        document.removeEventListener("touchmove", this.handleTouchMove);
        document.removeEventListener("touchend", this.handleTouchEnd);
        document.removeEventListener("touchcancel", this.handleTouchEnd);
    }
}
